import { syncWindowDefinitions, defaultSyncWindowDefinition } from '../../syncWindowDefinitions';

class SyncWindowSelectorController {
  constructor(
    $q,
    $timeout,
    $translate,
    $scope,
    showConfirmModal,
    DataSyncService,
    LogService
  ) {
    "ngInject";
    this.$q = $q;
    this.$timeout = $timeout;
    this.$translate = $translate;
    this.$scope = $scope;
    this.showConfirmModal = showConfirmModal;
    this.DataSyncService = DataSyncService;
    this.LogService = LogService;

    this._selectedSyncWindowDefinition = defaultSyncWindowDefinition;
    this._syncWindowDefinitions = [
      { name: 'one', translationKey: 'one' },
      { name: 'two', translationKey: 'SYNC_WINDOW_MEDIUM' },
    ];
    this._syncWindowDefinitionsWithTranslations = [];
  }

  $onInit() {
    const promises = [];

    this.$scope.$on('syncWindowChanged', () => {
      const syncWindowDefinition = this.DataSyncService.syncWindowDefinition;
      this.LogService.debug(`SyncWindowSelectorController syncWindowChanged event detected`, syncWindowDefinition);
      this._selectedSyncWindowDefinition = syncWindowDefinition;
      this._selectedSyncWindowName = syncWindowDefinition.name;
    });

    // Set current value from DataSyncService
    this._selectedSyncWindowName = this.DataSyncService.syncWindowDefinition !== undefined
      ? this.DataSyncService.syncWindowDefinition.name
      : defaultSyncWindowDefinition.name;

    promises.push(this.$q.when(this._updateSyncWindowTranslations()));
    this.$scope.$on('LanguageServiceChangedLanguage', () => {
      this._updateSyncWindowTranslations();
    });

    return this.$q.all(promises);
  }

  get disabled() {
    return !this.DataSyncService.isOnline;
  }

  get syncWindowDefinitions() {
    return this._syncWindowDefinitions;
  }

  get syncWindowDefinitionsWithTranslations() {
    return this._syncWindowDefinitionsWithTranslations;
  }

  get selectedSyncWindowName() {
    return this._selectedSyncWindowName;
  }

  get selectedSyncWindowDefinitionWithTranslations() {
    return this.syncWindowDefinitionsWithTranslations.find(d => d.name === this.selectedSyncWindowName);
  }

  set selectedSyncWindowName(selectedSyncWindowName) {
    const previousSyncWindowName = this._selectedSyncWindowName;
    this._selectedSyncWindowName = selectedSyncWindowName;
    if (this.DataSyncService
      && this.DataSyncService.syncWindowDefinition
      && selectedSyncWindowName !== this.DataSyncService.syncWindowDefinition.name
    ) {
      this.$timeout(() => this.confirmSyncWindowChange(previousSyncWindowName, selectedSyncWindowName));
    }
  }

  // just for convenience calling from template
  setSelectedSyncWindowName(selectedSyncWindowName) {
    this.selectedSyncWindowName = selectedSyncWindowName;
  }

  set cssClass(c) {
    this._cssClass = c;
  }

  get cssClass() {
    return this._cssClass;
  }

  get dropdownButtonText() {
    const selectedSyncWindowDefinition = this.selectedSyncWindowDefinitionWithTranslations;
    if (selectedSyncWindowDefinition) {
      return selectedSyncWindowDefinition.translatedWithoutParens;
    }
    console.error("No selected sync window?");
    return 'Select sync window';
  }

  confirmSyncWindowChange(previousSyncWindowName, selectedSyncWindowName) {
    const previousSyncWindowDefinition = syncWindowDefinitions[previousSyncWindowName];
    const selectedSyncWindowDefinition = syncWindowDefinitions[selectedSyncWindowName];
    this.LogService.debug(`SyncWindowSelectorController.confirmSyncWindowChange() from ${previousSyncWindowDefinition.name} to ${selectedSyncWindowDefinition.name}`);

    const descTranslationKey = selectedSyncWindowDefinition.translationKey + '_DESCRIPTION';
    return this.$translate(descTranslationKey)
      .catch(error => {
        // Swallow translation error
        this.LogService.warn(`Error translating ${descTranslationKey}:`, error);
        return `Are you sure you want to change data sync window to '${selectedSyncWindowDefinition.name}'?`;
      })
      .then(description => this.showConfirmModal(description))
      .catch(error => {
        this.LogService.debug(`SyncWindowSelectorController.confirmSyncWindowChange() rejected or error`);
        this._selectedSyncWindowName = previousSyncWindowName;
        return this.$q.reject(error);
      })
      .then(() => {
        this.LogService.debug(`SyncWindowSelectorController.confirmSyncWindowChange() accepted change from ${previousSyncWindowDefinition.name} to ${selectedSyncWindowDefinition.name}!`);
        this.DataSyncService.syncWindowDefinition = selectedSyncWindowDefinition;
        if (this.DataSyncService.isLoggedIn) {
          this.LogService.debug(`SyncWindowSelectorController.confirmSyncWindowChange() triggering sync`);
          this.DataSyncService.sync();
        } else {
          this.LogService.debug(`SyncWindowSelectorController.confirmSyncWindowChange() user not logged in so not refreshing data..`);
        }
      });
  }

  _updateSyncWindowTranslations() {
    const promises = [];

    // Create array of translated sync definitions
    // (translated values will be populated by promise)
    this._syncWindowDefinitionsWithTranslations = Object.keys(syncWindowDefinitions).map(name => {
      const placeholder = `TRANSLATING: ${syncWindowDefinitions[name].translationKey}`;
      const translatedDefinition = {
        ...syncWindowDefinitions[name],
        translated: placeholder,
        translatedWithoutParens: placeholder,
      };
      promises.push(
        this.$q.when(this.$translate(syncWindowDefinitions[name].translationKey))
          .then(translated => {
            translatedDefinition.translated = translated;
            translatedDefinition.translatedWithoutParens = translated
              .toString()
              .replace(/\s?\(.*\)/, '');
          })
      );
      return translatedDefinition;
    });

    return this.$q.all(promises)
      .then(() => this._syncWindowDefinitionsWithTranslations);
  }
}

export default SyncWindowSelectorController;
